import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, Table, Text } from "@marekl/adminer/components";
import { formatDate } from "@marekl/adminer/format";
import type { Competition } from "../../../lib/queries/competitions";
import { useTeamEntries } from "../../../lib/queries/entries";
import {
  COMPETITION_TYPE_LABELS,
  countdownIfNear,
  formatCompetitionDates,
} from "../../../lib/competitionState";
import { clubEntryBlock } from "../../../lib/entryEligibility";
import styles from "./DashboardPage.module.css";
import { RowLink } from "../../../components/RowLink";
import type { ID } from "../../../lib/id";

// „Nadcházející soutěže" from the club's side — what a vedoucí actually does
// in season: which uzávěrka is closest and whether we are on it. Entry counts
// are grouped client-side from the team's own entry list;
// `competition.entry_count` is the field-wide total and answers a different
// question.
//
// The team name rides in the subtitle, and it earns its place twice over: a
// leader of two teams would otherwise get two identically titled cards, and a
// správce who also leads a club sees this card next to the federation-wide
// one of the same name.

const MAX_ROWS = 5;

export interface TeamCompetitionsCardProps {
  teamId: ID;
  teamName: string;
  competitions: Competition[];
  isLoading?: boolean;
  error?: unknown;
  /** Nobody registered in the active season (`state_issues`), which shuts
   * the team out of every MČR start; TeamReadinessCard says why. */
  notRegistered?: boolean;
  /** Whether the team belongs to ČNSMS. A team outside it is refused at MČR
   * whatever its roster says, and `state_issues` is empty for it, so
   * „not registered" alone would read as „ready". */
  teamIsCmf?: boolean;
}

interface Row {
  competition: Competition;
  ours: number;
}

// The entry query lives inside the component rather than in the page: a
// leader may hold more than one team, and a hook cannot be called in a loop.
// One instance per team is the only shape that works.
export function TeamCompetitionsCard({
  teamId,
  teamName,
  competitions,
  isLoading,
  error,
  notRegistered = false,
  teamIsCmf = true,
}: TeamCompetitionsCardProps) {
  const teamEntries = useTeamEntries(teamId);
  const entries = teamEntries.data ?? [];
  const now = dayjs();
  const today = now.format("YYYY-MM-DD");

  const ours = new Map<ID, number>();
  for (const e of entries) {
    if (e.status !== "submitted") continue;
    ours.set(e.competition_id, (ours.get(e.competition_id) ?? 0) + 1);
  }

  // Upcoming first — a competition already run is history, and history has
  // its own page. A two-day event counts as upcoming on its first day.
  const rows: Row[] = competitions
    .filter((c) => c.status !== "draft" && (c.date_to ?? c.date) >= today)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, MAX_ROWS)
    .map((c) => ({ competition: c, ours: ours.get(c.id) ?? 0 }));

  return (
    <Card>
      <Card.Header
        title="Nadcházející soutěže"
        subtitle={teamName}
        subtitlePlacement="right"
        actions={
          <Link to={`/teams/${teamId}/souteze`} className={styles.action}>
            Zobrazit vše
          </Link>
        }
      />
      <Card.Content padding="none">
        <Table<Row>
          rows={rows}
          rowKey={(r) => r.competition.id}
          isLoading={isLoading || teamEntries.isLoading}
          error={error ?? teamEntries.error}
          cards="sm"
          empty={
            <EmptyState
              title="Žádná nadcházející soutěž"
              description="V této sezóně už nejsou vypsané žádné další soutěže."
            />
          }
          columns={[
            {
              key: "date",
              header: "Datum",
              render: (r) => (
                <>
                  <div>{formatCompetitionDates(r.competition)}</div>
                  {r.competition.venue && <Text variant="muted">{r.competition.venue}</Text>}
                </>
              ),
            },
            {
              key: "name",
              header: "Soutěž",
              primary: true,
              render: (r) => (
                <>
                  <div>
                    <RowLink to={`/teams/${teamId}/souteze/${r.competition.id}`}>
                      {r.competition.name}
                    </RowLink>
                  </div>
                  <Text variant="muted">
                    {r.competition.entry_deadline
                      ? `Uzávěrka ${formatDate(r.competition.entry_deadline)}${
                          countdownIfNear(r.competition.entry_deadline, now)
                            ? ` · ${countdownIfNear(r.competition.entry_deadline, now)}`
                            : ""
                        }`
                      : COMPETITION_TYPE_LABELS[r.competition.type]}
                  </Text>
                </>
              ),
            },
            {
              key: "ours",
              header: "Naše přihlášky",
              render: (r) =>
                r.ours > 0 ? (
                  <Badge variant="ok">{r.ours}</Badge>
                ) : (
                  <Badge variant="muted">0</Badge>
                ),
            },
            {
              key: "actions",
              header: "",
              render: (r) =>
                r.ours > 0 ? (
                  <Table.Actions>
                    <Table.Action appearance="button" to={`/teams/${teamId}/souteze/${r.competition.id}`}>
                      Otevřít
                    </Table.Action>
                  </Table.Actions>
                ) : r.competition.entry_phase === "open" &&
                  clubEntryBlock(r.competition.type, teamIsCmf, !notRegistered) === null ? (
                  <Table.Actions>
                    <Table.Action
                      appearance="button"
                      to={`/teams/${teamId}/souteze/${r.competition.id}/nova-prihlaska`}
                    >
                      Přihlásit
                    </Table.Action>
                  </Table.Actions>
                ) : (
                  <Text variant="muted">—</Text>
                ),
            },
          ]}
        />
      </Card.Content>
    </Card>
  );
}
